import FoodSafetyReportRegisters from './FoodSafetyReportRegisters'
import { Button, Drawer } from 'antd'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components/macro'
import {
  FOOD_SAFETY_HISTORY_LOCKED_CODE,
  FOOD_SAFETY_SECONDARY_TEXT,
  FOOD_SAFETY_SECTION,
  FOOD_SAFETY_SURFACE,
  foodSafetyDrawerWidth,
} from 'src/constants/foodSafety'
import { fetchFoodSafetyReportDay } from 'src/utils/foodSafetyApi'
import {
  formatReportDayItemName,
  formatReportDayItemStatus,
  formatReportDayLog,
  formatReportDaySummary,
  formatReportDayTitle,
  listReportDaySections,
  reportDayItemTone,
  reportDayPanelDomId,
  reportDaySectionExceptions,
} from 'src/utils/foodSafetyReportDay'
import {
  canActionReportException,
  formatReportCorrectiveActionLine,
  formatReportExceptionHeadline,
  formatReportExceptionStatus,
  formatReportRecheckLine,
  isReportExceptionOpen,
  sortReportExceptionsUnresolvedFirst,
} from 'src/utils/foodSafetyReportExceptions'
import {
  foodSafetyReportCellMark,
  foodSafetyReportCellTitle,
  foodSafetyReportCellTone,
  foodSafetyReportsSectionHref,
} from 'src/utils/foodSafetyReports'

const LogLine = ({ section, log, testId, withName = false, itemName }) => {
  const line = formatReportDayLog(section, log, { withName, itemName })
  if (section === FOOD_SAFETY_SECTION.DELIVERIES && !log?.payload?.skipped) {
    return (
      <DeliveryLog data-testid={testId}>
        <LogWhen>{line.when || '—'}</LogWhen>
        <span>
          <strong>Supplier:</strong> {line.supplier}
        </span>
        {line.deliveryReference ? (
          <Muted>{line.deliveryReference}</Muted>
        ) : null}
        <span>
          <strong>Product checked:</strong> {line.product}
        </span>
        {line.originalDeliveryName ? (
          <Muted>Recorded as: {line.originalDeliveryName}</Muted>
        ) : null}
        <span>
          <strong>Reading / decision:</strong> {line.deliveryReading}
          {line.flagged ? <Flag> · outside range</Flag> : null}
        </span>
        {line.who ? <Muted>{line.who}</Muted> : null}
        {line.entryAudit ? <Muted>{line.entryAudit}</Muted> : null}
        {line.notes ? <Notes>Notes: {line.notes}</Notes> : null}
      </DeliveryLog>
    )
  }
  return (
    <LogRow
      data-cooking={
        section === FOOD_SAFETY_SECTION.COOKING_REHEATING ? 'true' : undefined
      }
      data-flagged={line.flagged ? 'true' : 'false'}
      data-testid={testId}
    >
      <LogWhen>{line.when || '—'}</LogWhen>
      {section === FOOD_SAFETY_SECTION.COOKING_REHEATING ? (
        <span>{line.cookingKind}</span>
      ) : null}
      <span>
        {line.reading}
        {line.flagged ? <Flag> · outside range</Flag> : null}
        {line.who ? <Muted> · {line.who}</Muted> : null}
        {line.entryAudit ? <Muted>{line.entryAudit}</Muted> : null}
        {line.notes ? <Notes>{line.notes}</Notes> : null}
      </span>
    </LogRow>
  )
}

const CookingLogHeader = ({ section }) =>
  section === FOOD_SAFETY_SECTION.COOKING_REHEATING ? (
    <LogRow data-cooking='true'>
      <strong>Time</strong>
      <strong>Cook / Reheat</strong>
      <strong>Reading / staff</strong>
    </LogRow>
  ) : null

/**
 * One day of the weekly report in a side panel, so the grid stays where it
 * is. Readings are shown as recorded; nothing here edits them.
 */
const FoodSafetyReportDayPanel = ({
  venueId,
  localDate,
  focusSection,
  open,
  onClose,
  afterVisibleChange,
  onLocked,
  fetchDay = fetchFoodSafetyReportDay,
  readOnly = false,
}) => {
  const [day, setDay] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const requestRef = useRef(0)
  const onLockedRef = useRef(onLocked)
  onLockedRef.current = onLocked

  const load = useCallback(async () => {
    if (!venueId || !localDate) return
    const request = ++requestRef.current
    setLoading(true)
    setError(null)
    try {
      const data = await fetchDay(venueId, { date: localDate })
      if (request !== requestRef.current) return
      setDay(data)
    } catch (err) {
      if (request !== requestRef.current) return
      if (err?.code === FOOD_SAFETY_HISTORY_LOCKED_CODE) {
        onLockedRef.current?.(err.historyAccess)
        return
      }
      setDay(null)
      setError(err?.message || 'Could not load this day.')
    } finally {
      if (request === requestRef.current) setLoading(false)
    }
  }, [fetchDay, localDate, venueId])

  useEffect(() => {
    setDay(null)
    load()
  }, [load])

  useEffect(() => {
    if (!day || !focusSection) return
    const el = document.getElementById(reportDayPanelDomId(focusSection))
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'start' })
    }
  }, [day, focusSection])

  const sections = day ? listReportDaySections(day, { focusSection }) : []
  const summary = day ? formatReportDaySummary(day.summary) : ''
  const fallbackHref = readOnly
    ? null
    : foodSafetyReportsSectionHref(venueId, focusSection, localDate)

  return (
    <Drawer
      title={formatReportDayTitle(localDate)}
      width={foodSafetyDrawerWidth(460)}
      visible={open}
      onClose={onClose}
      afterVisibleChange={afterVisibleChange}
      destroyOnClose
    >
      <Body data-testid='fs-day-panel' data-date={localDate}>
        {loading && !day ? (
          <Muted data-testid='fs-day-panel-loading'>Loading this day…</Muted>
        ) : null}

        {error ? (
          <ErrorBox role='alert' data-testid='fs-day-panel-error'>
            <span>Couldn’t load this day. {error}</span>
            <ErrorActions>
              <Button
                size='small'
                onClick={load}
                data-testid='fs-day-panel-retry'
              >
                Try again
              </Button>
              {fallbackHref ? (
                <Link to={fallbackHref}>Open the section instead</Link>
              ) : null}
            </ErrorActions>
          </ErrorBox>
        ) : null}

        {day ? (
          <>
            {summary ? (
              <Summary data-testid='fs-day-panel-summary'>{summary}</Summary>
            ) : null}
            <FoodSafetyReportRegisters
              registers={day.registers}
              venueId={venueId}
            />
            {sections.length === 0 &&
            !day.registers?.training?.length &&
            !day.registers?.visitors?.length &&
            !day.registers?.trainingReminders?.length ? (
              <Muted data-testid='fs-day-panel-empty'>
                Nothing was due or logged this day.
              </Muted>
            ) : null}
            {sections.map(row => {
              const exceptions = sortReportExceptionsUnresolvedFirst(
                reportDaySectionExceptions(day, row.section),
              )
              const href = readOnly
                ? null
                : foodSafetyReportsSectionHref(venueId, row.section, localDate)
              return (
                <SectionBlock
                  key={row.section}
                  id={reportDayPanelDomId(row.section)}
                  data-focus={row.section === focusSection ? 'true' : 'false'}
                  data-testid={`fs-day-section-${row.section}`}
                  aria-labelledby={`${reportDayPanelDomId(row.section)}-title`}
                >
                  <SectionHead>
                    <SectionMark
                      data-tone={foodSafetyReportCellTone(row.cell?.status)}
                      aria-hidden
                    >
                      {foodSafetyReportCellMark(row.cell)}
                    </SectionMark>
                    <SectionTitle
                      id={`${reportDayPanelDomId(row.section)}-title`}
                    >
                      {row.label}
                    </SectionTitle>
                    <Muted>{foodSafetyReportCellTitle(row.cell || {})}</Muted>
                  </SectionHead>

                  {(row.items || []).map(item => (
                    <Item
                      key={item.definitionId}
                      data-testid={`fs-day-item-${item.definitionId}`}
                    >
                      <ItemHead>
                        <strong>{formatReportDayItemName(item)}</strong>
                        <StatusPill
                          data-tone={reportDayItemTone(item.status, {
                            logs: item.logs,
                          })}
                        >
                          {formatReportDayItemStatus(item.status, {
                            scheduleMode: item.scheduleMode,
                            logs: item.logs,
                          })}
                        </StatusPill>
                      </ItemHead>
                      {item.logs?.length ? (
                        <CookingLogHeader section={row.section} />
                      ) : null}
                      {(item.logs || []).map(log => (
                        <LogLine
                          key={String(log._id)}
                          section={row.section}
                          log={log}
                          itemName={item.name}
                          testId={`fs-day-log-${log._id}`}
                        />
                      ))}
                    </Item>
                  ))}

                  {(row.otherLogs || []).length ? (
                    <Item data-testid={`fs-day-other-${row.section}`}>
                      <ItemHead>
                        <strong>Also logged</strong>
                      </ItemHead>
                      <CookingLogHeader section={row.section} />
                      {row.otherLogs.map(log => (
                        <LogLine
                          key={String(log._id)}
                          section={row.section}
                          log={log}
                          withName
                          testId={`fs-day-log-${log._id}`}
                        />
                      ))}
                    </Item>
                  ) : null}

                  {exceptions.map(exception => {
                    const recheck = formatReportRecheckLine(exception.recheck)
                    return (
                      <ExceptionBox
                        key={exception._id}
                        data-open={
                          isReportExceptionOpen(exception) ? 'true' : 'false'
                        }
                        data-testid={`fs-day-exception-${exception._id}`}
                      >
                        <span>
                          {formatReportExceptionHeadline(exception)}
                          <Muted>
                            {' '}
                            · {formatReportExceptionStatus(exception)}
                          </Muted>
                        </span>
                        {(exception.correctiveActions || []).map((a, i) => (
                          <Muted key={`${exception._id}-a-${i}`}>
                            {formatReportCorrectiveActionLine(a)}
                          </Muted>
                        ))}
                        {recheck ? <Muted>{recheck}</Muted> : null}
                        {canActionReportException(exception) && href ? (
                          <Link to={href}>Record corrective action</Link>
                        ) : null}
                      </ExceptionBox>
                    )
                  })}

                  {href ? (
                    <OpenSection
                      to={href}
                      data-testid={`fs-day-open-${row.section}`}
                    >
                      Open {row.label}
                    </OpenSection>
                  ) : null}
                </SectionBlock>
              )
            })}
          </>
        ) : null}
      </Body>
    </Drawer>
  )
}

export default FoodSafetyReportDayPanel

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const Muted = styled.span`
  display: block;
  font-size: 0.8rem;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};

  span > & {
    display: inline;
  }
`

const Summary = styled.p`
  margin: 0;
  font-size: 0.85rem;
  font-weight: 600;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const ErrorBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  padding: 0.6rem 0.75rem;
  border: 1px solid ${FOOD_SAFETY_SURFACE.STATUS_ATTENTION};
  border-radius: ${FOOD_SAFETY_SURFACE.CONTROL_RADIUS};
  background: #fff7f0;
  font-size: 0.85rem;
`

const ErrorActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`

const SectionBlock = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  padding: 0.6rem 0.75rem;
  border: 1px solid ${({ theme }) => theme.colours.panSilver || '#eee'};
  border-radius: ${FOOD_SAFETY_SURFACE.CONTROL_RADIUS};
  scroll-margin-top: 0.5rem;

  &[data-focus='true'] {
    border-color: ${FOOD_SAFETY_SURFACE.LIME_EDGE};
    box-shadow: inset 3px 0 0 ${FOOD_SAFETY_SURFACE.LIME_EDGE};
  }
`

const SectionHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.2rem 0.5rem;
`

const SectionTitle = styled.h4`
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
`

const SectionMark = styled.span`
  display: inline-flex;
  justify-content: center;
  min-width: 1.4rem;
  font-weight: 700;

  &[data-tone='ok'] {
    color: ${FOOD_SAFETY_SURFACE.STATUS_DONE};
  }
  &[data-tone='alert'],
  &[data-tone='warn'] {
    color: ${FOOD_SAFETY_SURFACE.STATUS_ATTENTION};
  }
  &[data-tone='muted'],
  &[data-tone='paused'],
  &[data-tone='locked'] {
    color: ${FOOD_SAFETY_SECONDARY_TEXT};
  }
`

const Item = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding-top: 0.35rem;
  border-top: 1px dashed ${({ theme }) => theme.colours.panSilver || '#eee'};
`

const ItemHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.5rem;
  font-size: 0.9rem;
`

const StatusPill = styled.span`
  flex-shrink: 0;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 0.05rem 0.45rem;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.colours.panSilver || '#ddd'};
  color: ${FOOD_SAFETY_SECONDARY_TEXT};

  &[data-tone='ok'] {
    border-color: ${FOOD_SAFETY_SURFACE.LIME_EDGE};
    background: ${FOOD_SAFETY_SURFACE.LIME_WASH};
    color: #135200;
  }
  &[data-tone='alert'] {
    border-color: ${FOOD_SAFETY_SURFACE.STATUS_ATTENTION};
    background: #fff2e8;
    color: ${FOOD_SAFETY_SURFACE.STATUS_ATTENTION};
  }
  &[data-tone='paused'] {
    background: #f5f5f5;
  }
`

const LogRow = styled.div`
  display: grid;
  grid-template-columns: 3rem 1fr;
  &[data-cooking='true'] {
    grid-template-columns: 3rem 6rem minmax(0, 1fr);
  }
  gap: 0.5rem;
  font-size: 0.85rem;
`

const LogWhen = styled.span`
  font-variant-numeric: tabular-nums;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const Flag = styled.span`
  font-weight: 600;
  color: ${FOOD_SAFETY_SURFACE.STATUS_ATTENTION};
`

const Notes = styled.span`
  display: block;
  font-size: 0.8rem;
  font-style: italic;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const ExceptionBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  padding: 0.4rem 0.6rem;
  font-size: 0.85rem;
  border-left: 3px solid ${FOOD_SAFETY_SURFACE.STATUS_DONE};
  background: #fafafa;

  &[data-open='true'] {
    border-left-color: ${FOOD_SAFETY_SURFACE.STATUS_ATTENTION};
    background: #fffaf2;
  }
`

const OpenSection = styled(Link)`
  align-self: flex-start;
  font-size: 0.85rem;
  font-weight: 600;
`

const DeliveryLog = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding: 0.35rem 0;
  font-size: 0.85rem;
  overflow-wrap: anywhere;
`
